import { DictionaryRecordContextProvider } from "@/components/dictionaries/DictionaryContext";
import DictionaryLoader from "@/components/dictionaries/DictionaryLoader";
import DictionaryPageHeader from "@/components/dictionaries/DictionaryPageHeader";
import { useToast } from "@/components/ui/use-toast";
import { Module } from "@/constants";
import { DictionaryContext } from "@/containers/dictionaries/types";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { ApiError } from "@/services/api";
import { zodResolver } from "@hookform/resolvers/zod";
import { MutationFunction, useMutation, useQuery } from "@tanstack/react-query";
import { exists } from "i18next";
import { DefaultValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useFormResetWithQueryData } from "@/hooks/useFormResetWithQueryData";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useAgreementCompaniesDictionary } from "@/modules/agreement-companies/hooks/useAgreementCompaniesDictionary";
import {
  AgreementCompany,
  AgreementCompanyUpdate,
} from "@/types/agreementCompany";
import { agreementCompanyUpdate } from "@/schemas/agreementCompany";
import AgreementCompanyForm from "@/modules/agreement-companies/components/AgreementCompanyForm";
import AgreementCompanyEditorFooter from "./AgreementCompanyEditorFooter";

export default function AgreementCompanyEditor({ id }: DictionaryContext) {
  const { t } = useTranslation();
  const config = useAgreementCompaniesDictionary();
  const navigate = useNavigate();
  const { toast } = useToast();
  const labels = config.getLabels(t);
  const { updateQueryDataForExistingItem, invalidateListQuery } =
    useQueryCacheUpdater(Module.AgreementCompanies);
  const { can_update, can_delete } = useModulePermissions();

  const query = useQuery<AgreementCompany>({
    queryKey: [config.ns, config.id, id],
    queryFn: async () => config.service.get(id),
  });

  const mutationFn: MutationFunction<
    AgreementCompany,
    AgreementCompanyUpdate
  > = async (values) => config.service.patch(id, values);

  const deleteMutationFn: MutationFunction<void, void> = async () =>
    config.service.delete(id);

  const deleteMutation = useMutation({
    mutationFn: deleteMutationFn,
    onSuccess: () => {
      toast({
        title: t("agreementCompanies.success.delete"),
      });
      invalidateListQuery();
      navigate(config.path);
    },
    onError: () => {
      toast({
        title: t("agreementCompanies.errors.delete"),
        variant: "destructive",
      });
    },
  });

  const mutation = useMutation<
    AgreementCompany,
    ApiError,
    AgreementCompanyUpdate
  >({
    mutationFn,
    onSuccess: (response) => {
      const id = response.id;
      navigate(`${config.path}/${id}`);
      toast({
        title: labels.success.update,
      });
      updateQueryDataForExistingItem(response);
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: labels.errors.update,
        description,
        variant: "destructive",
      });
    },
  });

  const form = useForm<AgreementCompanyUpdate>({
    resolver: zodResolver(agreementCompanyUpdate),
    defaultValues: {} as DefaultValues<AgreementCompanyUpdate>,
    disabled: mutation.isPending || deleteMutation.isPending || !can_update,
  });

  useFormUnsavedChangesWarning(form);
  useFormResetWithQueryData(form, query);

  return (
    <DictionaryRecordContextProvider
      value={{
        query,
        config,
        mutation,
        form,
        id,
        deleteMutation: can_delete ? deleteMutation : undefined,
      }}
    >
      <DictionaryPageHeader />
      {query.isLoading && <DictionaryLoader />}
      {!query.isLoading && (
        <AgreementCompanyForm footer={<AgreementCompanyEditorFooter />} />
      )}
    </DictionaryRecordContextProvider>
  );
}
